import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, Lock, BellRinging, BellSlash, Warning, CaretDown } from '@phosphor-icons/react';

import type { ManifestSection, SettingsManifest } from '@/lib/settingsApi';
import {
  fetchNotificationPreferences,
  updateNotificationPreference,
  type NotificationChannel,
  type NotificationPreferences,
} from '@/lib/meApi';
import {
  currentPushPermission, hasActiveSubscription, enableBrowserNotifications,
  disableBrowserNotifications, pushSupported, type PushPermission,
} from '@/lib/push';
import { Panel, SaveIndicator, SectionIntro, Toggle, useSaveState, useSettingLookup, messageOf } from './primitives';

/**
 * Notifications — what you are told about, and how it reaches you.
 *
 * THE CATALOGUE COMES FROM THE SERVER. The page this replaces listed ten notification types
 * written out in React; the system sends forty-three. The other thirty-three could not be seen
 * or switched off, and nothing said so — a barrister who turned everything off here still
 * received most of what the platform sends. Adding a type to the enum now makes it appear on
 * this screen with a working default, because there is no second list to remember to update.
 *
 * THREE CHANNELS: in the app, as a browser notification, and (4C-6) by email. The email column
 * only appears against the types that actually support it — the curated, low-volume, high-value
 * kinds where an off-app copy earns its place — and it is opt-in: off until the member turns it on,
 * except for the "always on" obligations. A type with no email channel shows "—", never a dead
 * toggle over something that cannot happen.
 */

function ChannelHeader() {
  return (
    // `pb-2` with no top padding sat the labels hard against the category header's rule above,
    // so the row read as clipped. `py-2.5` matches the rhythm of the rows beneath it.
    <div className="flex items-center gap-3 border-b border-border px-5 py-2.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground sm:gap-6 sm:text-[11px]">
      <span className="min-w-0 flex-1">Notify me about</span>
      <span className="w-12 text-center sm:w-14">In app</span>
      <span className="w-12 text-center sm:w-14">Browser</span>
      <span className="w-12 text-center sm:w-14">Email</span>
    </div>
  );
}

function TypeRow({
  label,
  mandatory,
  channels,
  enabled,
  pending,
  onChange,
  pushUnavailable,
}: {
  label: string;
  mandatory: boolean;
  channels: NotificationChannel[];
  enabled: Record<NotificationChannel, boolean>;
  pending: NotificationChannel | null;
  onChange: (channel: NotificationChannel, value: boolean) => void;
  pushUnavailable: boolean;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-border/60 px-5 py-2.5 last:border-0 sm:gap-6">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2 text-sm text-foreground">
          {/* Wraps on a phone rather than truncating: a person cannot decide whether to switch
              something off if the end of the sentence is hidden behind an ellipsis. */}
          <span className="sm:truncate">{label}</span>
          {mandatory && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
              title="This one always reaches you — it carries a professional or account obligation."
            >
              <Lock size={9} /> Always on
            </span>
          )}
        </div>
      </div>

      {(['inApp', 'push', 'email'] as const).map((channel) => (
        <div key={channel} className="flex w-12 flex-col items-center gap-1 sm:w-14">
          {!channels.includes(channel) ? (
            // Explicit, not an off switch: this type has no such channel at all.
            <span className="text-xs text-muted-foreground/50" title="Not available for this notification">—</span>
          ) : pending === channel ? (
            <CircleNotch size={14} className="animate-spin text-muted-foreground/60" />
          ) : (
            <Toggle
              label={`${label} — ${channel === 'inApp' ? 'in app' : channel === 'push' ? 'browser' : 'email'}`}
              checked={enabled[channel] && !(channel === 'push' && pushUnavailable)}
              disabled={mandatory || (channel === 'push' && pushUnavailable)}
              onChange={(v) => onChange(channel, v)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/** The browser-level permission, which sits above every per-type push choice. */
function BrowserPushPanel({ onChanged }: { onChanged: () => void }) {
  const [permission, setPermission] = useState<PushPermission>(() => currentPushPermission());
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const { state, error, run } = useSaveState();

  useEffect(() => { void hasActiveSubscription().then(setSubscribed); }, []);

  if (!pushSupported()) {
    return (
      <Panel title="Browser notifications" description="Alerts that reach you when Jurivoxa is not the tab you are looking at">
        <p className="text-sm text-muted-foreground">This browser does not support them.</p>
      </Panel>
    );
  }

  const on = permission === 'granted' && subscribed === true;

  return (
    <Panel title="Browser notifications" description="Alerts that reach you when Jurivoxa is not the tab you are looking at">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2.5">
          {on ? <BellRinging size={18} className="mt-0.5 text-brand-600 dark:text-brand-400" />
              : <BellSlash size={18} className="mt-0.5 text-muted-foreground" />}
          <div>
            <p className="text-sm font-medium text-foreground">
              {on ? 'On for this browser' : 'Off for this browser'}
            </p>
            <p className="text-xs text-muted-foreground">
              {permission === 'denied'
                ? 'Your browser is blocking notifications for this site. Allow them in the address-bar padlock, then come back.'
                : 'Each browser you sign in from asks separately.'}
            </p>
          </div>
        </div>

        <button
          type="button"
          disabled={state === 'saving' || permission === 'denied'}
          onClick={() => void run(async () => {
            if (on) { await disableBrowserNotifications(); setSubscribed(false); }
            else {
              const next = await enableBrowserNotifications();
              setPermission(next);
              setSubscribed(await hasActiveSubscription());
            }
            onChanged();
          })}
          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
        >
          {state === 'saving' ? 'Working…' : on ? 'Turn off' : 'Turn on'}
        </button>
      </div>
      <div className="mt-2"><SaveIndicator state={state === 'saved' ? 'idle' : state} error={error} /></div>
    </Panel>
  );
}

export function NotificationsSection({ section }: { section: ManifestSection; manifest: SettingsManifest }) {
  const qc = useQueryClient();
  const setting = useSettingLookup(section);
  const matrix = setting('notifications.matrix');
  const [pending, setPending] = useState<{ type: string; channel: NotificationChannel } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pushPermission, setPushPermission] = useState<PushPermission>(() => currentPushPermission());
  /**
   * Which categories are open. All shut on arrival: eleven categories of several rows each made
   * this the longest page in Settings, and a person comes here to change one thing, not read it
   * end to end. Same arrow and same behaviour as the Security panels.
   */
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const toggleCategory = (id: string) =>
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['me-notification-preferences'],
    queryFn: fetchNotificationPreferences,
    enabled: !!matrix,
  });

  const save = useMutation({
    mutationFn: ({ type, channel, value }: { type: string; channel: NotificationChannel; value: boolean }) =>
      updateNotificationPreference(type, channel, value),
    onSuccess: (fresh: NotificationPreferences) => {
      qc.setQueryData(['me-notification-preferences'], fresh);
      setFailure(null);
    },
    onError: (err) => setFailure(messageOf(err)),
    onSettled: () => setPending(null),
  });

  const grouped = useMemo(() => {
    if (!data) return [];
    return data.categories
      .map((category) => ({
        ...category,
        types: data.types.filter((t) => t.category === category.id),
      }))
      .filter((c) => c.types.length > 0);
  }, [data]);

  // Per-type push choices are real, but they cannot deliver until the browser itself allows it.
  const pushUnavailable = pushPermission !== 'granted';

  if (!matrix) {
    return (
      <div className="space-y-5">
        <SectionIntro title="Notifications" description="What you are told about, and how it reaches you" />
        {setting('notifications.push') && <BrowserPushPanel onChanged={() => setPushPermission(currentPushPermission())} />}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionIntro title="Notifications" description="What you are told about, and how it reaches you" />

      {setting('notifications.push') && <BrowserPushPanel onChanged={() => setPushPermission(currentPushPermission())} />}

      {failure && (
        <p className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-50/50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
          <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
          {failure}
        </p>
      )}

      {isLoading && (
        <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading notification preferences">
          <CircleNotch size={20} className="animate-spin text-muted-foreground/50" />
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm font-medium text-foreground">Your notification preferences could not be loaded</p>
          <button onClick={() => void refetch()} className="mt-3 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">
            Try again
          </button>
        </div>
      )}

      {!matrix.canWrite && data && (
        <p className="text-xs text-muted-foreground">These are shown as they stand; they are not changed from here.</p>
      )}

      {grouped.map((category) => {
        const open = openCategories.has(category.id);
        return (
        <section key={category.id} className="rounded-xl border border-border bg-card">
          {/* The rule under the heading belongs to the content below it; with the category shut
              there is nothing to separate, so it goes too. */}
          <header className={`flex items-start justify-between gap-3 px-5 py-3.5 ${open ? 'border-b border-border' : ''}`}>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground">{category.label}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">{category.description}</p>
            </div>
            <button
              type="button"
              onClick={() => toggleCategory(category.id)}
              aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} ${category.label} notifications`}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
            >
              <span>{category.types.length}</span>
              <CaretDown size={12} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
          </header>
          {open && <ChannelHeader />}
          <div>
            {open && category.types.map((t) => (
              <TypeRow
                key={t.type}
                label={labelFor(t.type)}
                mandatory={t.mandatory || !matrix.canWrite}
                channels={t.channels}
                enabled={t.enabled}
                pushUnavailable={pushUnavailable}
                pending={pending?.type === t.type ? pending.channel : null}
                onChange={(channel, value) => {
                  setPending({ type: t.type, channel });
                  save.mutate({ type: t.type, channel, value });
                }}
              />
            ))}
          </div>
        </section>
        );
      })}
    </div>
  );
}

/**
 * Plain wording for a type name.
 *
 * A fallback, not a catalogue: an unrecognised type still renders, readably, from its own name.
 * That is the point — a new notification type must never be invisible here.
 */
const TYPE_LABEL: Record<string, string> = {
  hearing_reminder: 'A hearing is coming up',
  event_reminder: 'A diary entry is coming up',
  calendar_conflict: 'Two things clash in my diary',
  hearing_assigned: 'I am put into a hearing',
  hearing_outcome_pending: 'A hearing needs its outcome recorded',
  case_assigned: 'I am assigned to a matter',
  intake_received: 'New instructions arrive',
  matter_needs_allocation: 'A matter needs allocating',
  matter_inactive: 'A matter has gone quiet',
  judgment_reserved: 'Judgment is reserved',
  limitation_approaching: 'A limitation date is approaching',
  conflict_detected: 'A conflict is detected',
  conflict_check_required: 'A conflict check is required of me',
  brief_received: 'I am offered a brief',
  brief_accepted: 'A brief I offered is accepted',
  brief_declined: 'A brief I offered is declined',
  brief_withdrawn: 'A brief is withdrawn',
  brief_response_due: 'A brief needs my answer',
  brief_response_overdue: 'A brief answer is overdue',
  conference_requested: 'A conference is requested',
  conference_scheduled: 'A conference is scheduled',
  conference_cancelled: 'A conference is cancelled',
  conference_completed: 'A conference is completed',
  task_assigned: 'A task is assigned to me',
  task_due: 'A task falls due',
  task_completed: 'A task is completed',
  document_uploaded: 'Papers are added to my matter',
  message_received: 'I receive a message',
  fee_note_issued: 'A fee note is sent out',
  fee_note_paid: 'A fee note is paid',
  fee_note_pending: 'A fee note is waiting to go out',
  fee_note_overdue: 'A fee note is overdue',
  credit_note_issued: 'A credit note is issued',
  clerk_assigned: 'A clerk is assigned to me',
  clerk_unassigned: 'A clerk is unassigned',
  clerk_coordination_requested: 'My clerk asks me something',
  clerk_coordination_updated: 'A clerking request changes',
  clerk_coordination_cancelled: 'A clerking request is cancelled',
  availability_approved: 'My availability is approved',
  availability_rejected: 'My availability is declined',
  kyc_expiring: 'Client verification is expiring',
  policy_published: 'A chambers policy is published',
  system: 'Messages about my account',
};

function labelFor(type: string): string {
  return TYPE_LABEL[type] ?? type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}
